import { Component, Inject, Injectable, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { MatTableDataSource } from '@angular/material/table';
import { MenuExportReportService } from 'src/app/core/services/menu-export-report.service';
import { Router } from '@angular/router';
import { Message } from "src/app/core/class/message";
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS, DateAdapter, ErrorStateMatcher, NativeDateAdapter } from '@angular/material/core';

@Injectable()
export class InstantErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
      const isSubmitted = form && form.submitted;
      return !!(control && control.invalid && (control.touched || isSubmitted));
    }
  }
export class CustomDateAdapter extends NativeDateAdapter {
 format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'input') {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return super.format(date, displayFormat);
  }
}

export const MY_DATE_FORMATS = {
  parse: { dateInput: { month: 'short', year: 'numeric', day: 'numeric' } },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};
@Component({
    selector: 'app-report-dialog',
    templateUrl: './report-dialog.component.html',
    styleUrls: ['./report-dialog.component.scss'],
    providers: [
        { provide: DateAdapter, useClass: CustomDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
        { 
          provide: MAT_DATE_LOCALE, 
          useFactory: () => localStorage.getItem('__language') || 'vi-VN' 
        }
      ]
})
export class ReportDialogComponent implements OnInit {
    infoForm!: FormGroup;
    matcher = new InstantErrorStateMatcher();
    lastTouchedField: string = '';
    dataSource = new MatTableDataSource<any>([]);
    total: number;
    rawSelectedData: any[] = [];
    searchKey: string = '';
    selectedReportPeriodLabel: string = '';
    displayedColumns: string[] = ['stt', 'uuid', 'contractName', 'enterprises', 'creationTime'];
    listTargetType = [
        { value: 1, label: 'menu_export_report.dialog_create.target_options.enterprise' },
        { value: 2, label: 'menu_export_report.dialog_create.target_options.cooperative' },
        { value: 3, label: 'menu_export_report.dialog_create.target_options.organization' },
        { value: 4, label: 'menu_export_report.dialog_create.target_options.individual' }
    ];
    listReportPeriod: any = [
        { label: this.translate.instant("menu_export_report.first_half_year"), value: 1 },
        { label: this.translate.instant("menu_export_report.one_year"), value: 2 },
        { label: this.translate.instant("menu_export_report.ad_hoc"), value: 3 },
    ];
    constructor(
        private fb: FormBuilder,
        private toastr: ToastrService,
        private translate: TranslateService,
        public message: Message,
        private router: Router,
        public dialogRef: MatDialogRef<ReportDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private menuExportReportService: MenuExportReportService
    ) { }

    ngOnInit(): void {
        if (this.data && this.data.selectedData) {
            this.rawSelectedData = this.data.selectedData || [];
            this.dataSource.data = [...this.rawSelectedData];
            this.total = this.dataSource.data.length;
        }

        const currentTaxCode = (this.data && (this.data.taxCode || this.data.certificateNo))
            || localStorage.getItem('taxCode')
            || localStorage.getItem('certificateNo');

        if (!currentTaxCode) {
            const errorMsg = this.translate.instant('menu_export_report.dialog_create.validation.tax_code_not_found');
            this.toastr.error(errorMsg);
            this.dialogRef.close(false);
            return;
        }

        this.initForm(currentTaxCode);
    }

    onDateInputChange(fieldName: string, inputElement: any, control: any): void {
        if (fieldName) {
          this.lastTouchedField = fieldName;
        }
        if (control) {
          control.markAsTouched();
        }
      }
    applyFilter(event: any): void {
        const filterValue = event && event.target ? (event.target as HTMLInputElement).value : (event || '');
        this.searchKey = filterValue.trim().toLowerCase();

        if (!this.searchKey) {
            this.dataSource.data = [...this.rawSelectedData];
            return;
        }

        this.dataSource.data = this.rawSelectedData.filter(item => {
            const uuid = item.contractUUID ? String(item.contractUUID).toLowerCase() : '';
            const contractName = item.contractName ? String(item.contractName).toLowerCase() : '';
            const contractCode = item.contractCode ? String(item.contractCode).toLowerCase() : '';

            return uuid.includes(this.searchKey) ||
                contractName.includes(this.searchKey) ||
                contractCode.includes(this.searchKey);
        });
    }

    initForm(taxCode?: string): void {
        const reportData = this.data || {};

        const identifier = reportData.taxCode || reportData.certificateNo || taxCode || '';

        let initialYear: number;
        if (reportData.reportYear) {
            const parsedDate = new Date(reportData.reportYear);
            initialYear = !isNaN(parsedDate.getTime()) ? parsedDate.getFullYear() : Number(reportData.reportYear);
        } else {
            initialYear = new Date().getFullYear();
        }

        const rawPeriod = reportData.reportPeriod;

        const periodObj = this.listReportPeriod.find(item => item.value === Number(rawPeriod));
        this.selectedReportPeriodLabel = periodObj ? periodObj.label : '';

        const dateRange = this.calculateDateRange(
            reportData.reportPeriod,
            initialYear,
            reportData.fromDate,
            reportData.toDate
        );

        let reportYear: number = initialYear;
        if (dateRange.toDate) {
            const yearFromToDate = parseInt(dateRange.toDate.split('/').pop(), 10);
            if (!isNaN(yearFromToDate)) {
                reportYear = yearFromToDate;
            }
        }
        let initialReportDate: any = new Date();
        if (reportData.reportDate) {
          initialReportDate = new Date(reportData.reportDate);
          if (isNaN(initialReportDate.getTime())) {
            initialReportDate = new Date();
          }
        }
        this.infoForm = this.fb.group({
          certificateNo: [{ value: identifier, disabled: true }],
          reportName: [
            reportData.reportName || '', 
            [Validators.required, Validators.maxLength(255)]
          ],
          reportPeriod: [{ value: rawPeriod, disabled: true }],
          reportYear: [{ value: reportYear, disabled: true }],
          fromDate: [{ value: dateRange.fromDate, disabled: true }],
          toDate: [{ value: dateRange.toDate, disabled: true }],
          reportNo: [
            reportData.reportNo || '', 
            [Validators.maxLength(50)]
          ],
          reportDate: [initialReportDate, Validators.required],
          targetType: [
            reportData.targetType !== undefined && reportData.targetType !== null ? reportData.targetType : null, 
            Validators.required
          ]
        });
    }
    focusOut(fieldName: string): void {
        const control = this.infoForm.get(fieldName);
        if (control && control.value && typeof control.value === 'string') {
            control.setValue(control.value.trim());
        }
    }
    onSearchBlur(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input) {
            input.value = input.value.trim();
            this.searchKey = input.value.toLowerCase();
        }
    }
    onDateInput(event: Event, controlName: string): void {
        const input = event.target as HTMLInputElement;
        const value = input.value.trim();
        const control = this.infoForm.get(controlName);
        if (!control) return;

        if (!value) {
            return; 
        }

        // Tách ngày/tháng/năm
        const parts = value.split(/[/.-]/);
        let day = 0, month = 0, year = 0;

        if (parts.length === 3) {
            day = parseInt(parts[0], 10);
            month = parseInt(parts[1], 10) - 1;
            year = parseInt(parts[2], 10);
        } else {
            const digits = value.replace(/\D/g, '');
            if (digits.length === 8) {
                day = parseInt(digits.substring(0, 2), 10);
                month = parseInt(digits.substring(2, 4), 10) - 1;
                year = parseInt(digits.substring(4, 8), 10);
            }
        }

        const parsedDate = new Date(year, month, day);
        const isValid = parsedDate.getFullYear() === year &&
            parsedDate.getMonth() === month &&
            parsedDate.getDate() === day;

        if (isValid) {
            control.setValue(parsedDate, { emitEvent: false });
            control.setErrors(null);
        } else {
            control.setErrors({ invalidDate: true });
        }
    }

    onDateSelect(event: any, controlName: string): void {
        const control = this.infoForm.get(controlName);
        if (control && event.value) {
            control.setErrors(null);
        }
    }
    private formatDateToString(dateInput: any): string {
        if (!dateInput) return '';
        const date = new Date(dateInput);
        if (isNaN(date.getTime())) return '';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }
    private calculateDateRange(period: any, year: number, inputFromDate: any, inputToDate: any) {
        let fromDate: any = null;
        let toDate: any = null;

        if (period === 3 || period === 'DOT_XUAT') {
            fromDate = inputFromDate;
            toDate = inputToDate;
        } else if (period === 1 || period === 'SIX_MONTHS') {
            fromDate = new Date(year, 0, 1);
            toDate = new Date(year, 5, 30);
        } else if (period === 2 || period === 'FULL_YEAR') {
            fromDate = new Date(year, 0, 1);
            toDate = new Date(year, 11, 31);
        }

        return {
            fromDate: this.formatDateToString(fromDate),
            toDate: this.formatDateToString(toDate)
        };
    }

    onCancel(): void {
        this.dialogRef.close(false);
    }

    formatDateToYYYYMMDD(dateVal: any): string {
        if (!dateVal) return '';
      
        if (typeof dateVal === 'string' && dateVal.includes('/')) {
          const parts = dateVal.split('/');
          if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            return `${year}-${month}-${day}`;
          }
        }
      
        const d = new Date(dateVal);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`; 
        }
      
        return '';
      }
      onSubmit(): void {
        // 1. Kiểm tra Validate Form
        if (this.infoForm.invalid) {
          this.infoForm.markAllAsTouched();
          return;
        }
      
        const formVal = this.infoForm.getRawValue();
      
        let selectionMode = "ALL";
        if (formVal.selectionMode !== null && formVal.selectionMode !== undefined) {
          selectionMode = formVal.selectionMode;
        } else if (this.data && this.data.selectionMode !== null && this.data.selectionMode !== undefined) {
          selectionMode = this.data.selectionMode;
        } else {
          selectionMode = this.data && this.data.isExportAll ? "ALL" : "SELECTED";
        }
      
        // Lấy danh sách contractIds nếu là xuất bản ghi đã chọn
        const contractIds = (this.rawSelectedData || this.dataSource.data || []).map(
          (item: any) => item.uuid || item.id || item.contractId
        );
      
        // Tạo payload cơ bản
        const payload: any = {
          certificateNo: formVal.certificateNo,
          fromDate: this.formatDateToYYYYMMDD(formVal.fromDate),
          toDate: this.formatDateToYYYYMMDD(formVal.toDate),
          reportPeriod: Number(formVal.reportPeriod),
          reportYear: Number(formVal.reportYear),
          name: formVal.reportName,
          reportNumber: formVal.reportNo,
          reportDate: this.formatDateToYYYYMMDD(formVal.reportDate),
          objectType: Number(formVal.targetType),
          selectionMode: selectionMode
        };
      
        // Chỉ gắn contractIds nếu selectionMode === 1 (Xuất đã chọn)
        if (selectionMode === "SELECTED") {
          payload.contractIds = contractIds;
        }
      
        this.menuExportReportService.createReportMoha(payload).subscribe({
          next: (res: any) => {
            let responseData = res;
            if (typeof res === 'string') {
              try { 
                responseData = JSON.parse(res); 
              } catch (e) {
                console.error('Lỗi parse response JSON:', e);
              }
            }
        
            // 1. Kiểm tra trạng thái thành công từ response
            const isSuccess = responseData && (
              responseData.code === '200' || 
              responseData.code === 200 || 
              responseData.status === 'OK' || 
              responseData.success === true
            );
        
            if (isSuccess) {
              this.toastr.success(this.translate.instant('menu_export_report.messages.create_success'));
              this.dialogRef.close({
                action: 'SUBMIT',
                data: responseData
              });
              this.router.navigate(['/home-enterprise/menu-export-report']);
            } else {
              const errorMessage = responseData && (responseData.message || responseData.msg) 
                ? (responseData.message || responseData.msg)
                : this.translate.instant('menu_export_report.messages.create_error');
        
              this.toastr.error(errorMessage);
            }
          },
          error: (err: any) => {
            this.toastr.error(this.translate.instant('menu_export_report.messages.create_error'));
          }
        });
      }
    clearSearch(inputElement: HTMLInputElement): void {
        if (inputElement) {
            inputElement.value = '';
        }
        this.searchKey = '';
        this.dataSource.data = [...this.rawSelectedData];
    }
    getErrorDateMessageV2(pickerInput: string, isExpired: boolean = false, nameField: string): string {
        // 1. Validate Bắt buộc nhập (Required)
        if (!pickerInput || !pickerInput.trim()) {
          if (nameField === 'createdFromDate') {
            return this.message.getMessage('menu_export_report.created_from_date_required');
          }
          if (nameField === 'createdToDate') {
            return this.message.getMessage('menu_export_report.created_to_date_required');
          }
          if (nameField === 'reportDate') {
            return this.message.getMessage('menu_export_report.dialog_create.validation.report_date_required');
          }
          return '';
        }
      
        // 2. Validate Đúng định dạng (DD/MM/YYYY)
        const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (!regex.test(pickerInput)) {
          return this.message.getMessage('validation.format_of_date');
        }
      
        const [day, month, year] = pickerInput.split('/').map(Number);
        const date = new Date(year, month - 1, day);
        const isValid = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
      
        if (!isValid) {
          return this.message.getMessage('validation.format_of_date');
        }
      
        return '';
      }
}